import type { Lease, VmMetrics } from '@/api/types'
import {
  UNREACHABLE_WORD,
  isClosed,
  isUnreachable,
  leaseToneOf,
  leaseWord,
  vmToneOf,
  vmWord,
  type Tone,
} from '@/domain/lease'
import type { LeaseRow } from '../model'

// 관리자 VM 목록의 줄(`VmRow.tsx`)이 그리는 값과 목록(`views/VmList.tsx`)의 알약. 컴포넌트 파일은 컴포넌트만 내보내므로(react-refresh) 여기 따로 둔다.
// 목록을 세우고 찾는 것은 서버다 — `GET /leases` 의 `sort` 와 `q` 가 여기 함수와 같은 뜻의 값(VM 이름·총량·할당)을 본다.

/** 거르개 알약. 하나만 켜지고 `live`(`전체`)가 기본이다. */
export type View = 'live' | 'retired' | 'ended'

/** 자원 칸이 읽는 것 — 임대와 그 지금 읽기. */
export type MetricRow = { lease: Lease; m: VmMetrics | undefined }

// RAM 칸의 총량 — 게스트가 가진 메모리, 아직 읽지 못했으면 임대의 것. 서버의 `memory` 정렬도 게스트의 것, 없으면 신청한 것이다.
// DISK 칸은 지금 읽기가 아니라 임대의 할당이다(`domain/lease.ts` 의 `diskAllocatedGb`) — 서버의 `disk` 정렬도 그 값이다.
export const ramMb = ({ lease: l, m }: MetricRow) => m?.memory_total_mb ?? l.memory_mb

/** 상태 칸의 배지. 목록의 상태 거르개가 고르는 낱말도 이 배지가 말할 수 있는 낱말이다(`views/VmList.tsx`). */
export function badges({ lease: l, vm: v }: LeaseRow): { tone: Tone; word: string }[] {
  const out = [{ tone: leaseToneOf(l.state), word: leaseWord(l.state) }]
  if (v && v.state !== 'running') out.push({ tone: vmToneOf(v.state), word: vmWord(v.state) })
  if (isUnreachable(v)) out.push({ tone: 'warn', word: UNREACHABLE_WORD })
  return out
}

/**
 * VM 칸의 이름. 서버의 `name` 정렬과 찾기도 같은 이름을 본다.
 * 끝난 임대는 게스트가 지워져 VM 이 없다 — 그래도 배정 전이 아니라 임대의 이름이 그 VM 의 이름이다.
 * 이름이 없는 것은 아직 배정되지 않은 임대뿐이다.
 */
export function vmName({ lease: l, vm: v }: LeaseRow): string | null {
  if (v) return v.name
  return isClosed(l) ? l.name : null
}
